import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, Check } from 'lucide-react';

import { PredictionsResponseOutputItemsItem } from '@/api/generated';
import { SkuPredictionOverviewCard } from '@/components/blocks/shelf-detail/predictions-overview/sku-prediction-overview-card';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface SkuPredictionListProps {
  confirmedSkuMetadataId?: string;
  predictions: Array<PredictionsResponseOutputItemsItem>;
  onConfirmSkuPrediction: (skuMetadataId: string) => void;
  // The box is already labelled MISSING_SKU.
  noneSelected: boolean;
  onNoneOfTheAbove: () => void;
  classname?: string;
}

const NoneOfTheAboveCard: FC<{ selected: boolean; onClick: () => void }> = ({
  selected,
  onClick,
}) => {
  const { t } = useTranslation();
  return (
    <Card
      data-testid='prediction-none-of-the-above'
      role='button'
      className={cn(
        'relative aspect-square items-center justify-center border-dashed hover:cursor-pointer hover:border-[#FF6738]',
        { 'bg-accent': selected }
      )}
      onClick={onClick}>
      <CardContent className='text-muted-foreground flex flex-col items-center gap-2 text-center text-sm font-medium'>
        <Ban className='size-6' />
        {t('shelfDetail.noneOfTheAbove')}
      </CardContent>
      {selected && (
        <Badge
          className='absolute bottom-1 left-1'
          variant='annotated'
          aria-hidden>
          <Check className='text-success size-2' />
        </Badge>
      )}
    </Card>
  );
};

export const SkuPredictionOverview: FC<SkuPredictionListProps> = ({
  confirmedSkuMetadataId,
  predictions,
  onConfirmSkuPrediction,
  noneSelected,
  onNoneOfTheAbove,
  classname,
}: SkuPredictionListProps) => (
  <div
    className={cn(
      'animate-in fade-in-0 duration-300 grid min-h-0 grid-cols-2 xl:grid-cols-3 gap-2 overflow-y-scroll',
      classname
    )}>
    {predictions.map((item) => (
      <SkuPredictionOverviewCard
        className='aspect-square'
        key={item.skuMetadataId}
        onClick={onConfirmSkuPrediction}
        skuPrediction={item}
        confirmedSkuMetadataId={confirmedSkuMetadataId}
      />
    ))}
    <NoneOfTheAboveCard
      selected={noneSelected}
      onClick={onNoneOfTheAbove}
    />
  </div>
);
